import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { ToastService } from './toast.service';

@Injectable({
    providedIn: 'root'
})
export class DevToolsBlockerService {
    private isBrowser: boolean;
    private readonly detectionIntervalMs = 500;
    private readonly requiredConsecutiveDetections = 2;
    private readonly consoleProfileThresholdMs = 75;
    private readonly redirectDelayMs = 3000;
    private readonly warningToastDurationMs = 4000;
    private detectionIntervalId: ReturnType<typeof setInterval> | null = null;
    private consecutiveDetections = 0;
    private logoutInProgress = false;
    private authPromptInProgress = false;
    private devtoolsAccessGranted = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private userService: UserService,
        private toast: ToastService
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    enableBlock() {
        if (!this.isBrowser || !environment.devtoolsProtection) {
            return;
        }

        if (this.detectionIntervalId) {
            return;
        }

        document.addEventListener('contextmenu', this.preventRightClick);
        document.addEventListener('keydown', this.preventDevToolsShortcuts);
        this.detectionIntervalId = setInterval(this.detectAndHandleDevTools, this.detectionIntervalMs);
    }

    disableBlock() {
        if (this.isBrowser) {
            document.removeEventListener('contextmenu', this.preventRightClick);
            document.removeEventListener('keydown', this.preventDevToolsShortcuts);
            if (this.detectionIntervalId) {
                clearInterval(this.detectionIntervalId);
                this.detectionIntervalId = null;
            }
        }
    }

    private preventRightClick = (event: MouseEvent): void => {
        if (!this.hasDevToolsAccess()) {
            event.preventDefault();
        }
    }

    private preventDevToolsShortcuts = (event: KeyboardEvent): void => {
        if (this.logoutInProgress || this.hasDevToolsAccess()) {
            return;
        }

        if (
            event.key === 'F12' ||
            (event.ctrlKey && event.shiftKey && ['C', 'I', 'J'].includes(event.key.toUpperCase())) ||
            (event.ctrlKey && event.key.toUpperCase() === 'U') ||
            (event.metaKey && event.altKey && ['C', 'I', 'J', 'U'].includes(event.key.toUpperCase()))
        ) {
            event.preventDefault();
            event.stopPropagation();
            this.handleDevToolsDetected();
        }
    }

    private detectAndHandleDevTools = (): void => {
        if (this.logoutInProgress || this.authPromptInProgress) {
            return;
        }

        if (this.hasDevToolsAccess()) {
            this.consecutiveDetections = 0;
            return;
        }

        if (this.isDevToolsLikelyOpen()) {
            this.consecutiveDetections++;
        } else {
            this.consecutiveDetections = 0;
        }

        if (this.consecutiveDetections >= this.requiredConsecutiveDetections) {
            this.handleDevToolsDetected();
        }
    }

    private isDevToolsLikelyOpen(): boolean {
        return this.detectConsoleProfileDelay();
    }

    private detectConsoleProfileDelay(): boolean {
        const startedAt = performance.now();

        try {
            console.profile();
            console.profileEnd();
        } catch {
            return false;
        }

        return performance.now() - startedAt > this.consoleProfileThresholdMs;
    }

    private handleDevToolsDetected(): void {
        if (this.logoutInProgress || this.authPromptInProgress || this.hasDevToolsAccess()) {
            return;
        }

        this.authPromptInProgress = true;
        const enteredKey = window.prompt('Developer tools detected. Enter the authentication key:');
        this.authPromptInProgress = false;

        if (enteredKey === environment.devtoolsAuthKey) {
            this.devtoolsAccessGranted = true;
            this.consecutiveDetections = 0;
            this.toast.info('Developer tools access granted until the page is refreshed.', this.warningToastDurationMs);
            return;
        }

        this.logoutIfAuthenticated();
    }

    private hasDevToolsAccess(): boolean {
        return this.devtoolsAccessGranted;
    }

    private logoutIfAuthenticated(): void {
        if (this.logoutInProgress) {
            return;
        }

        this.logoutInProgress = true;
        this.disableBlock();
        localStorage.clear();
        sessionStorage.clear();
        this.toast.info('Developer tools were detected. Please log in again.', this.warningToastDurationMs);
        void this.userService.signout();
        setTimeout(() => {
            window.location.replace(environment.staticSiteUrl);
        }, this.redirectDelayMs);
    }
}
